import type { CrossChainQuote, CrossChainStep } from './types';

/**
 * Routing outcome describing how the selected quote relates to the API-ordered list.
 *
 * - `api_first_preferred`  – API rank-1 quote was already a preferred bridge; no override.
 * - `skipped_fallback_api_first` – API rank-1 was a fallback provider; we picked a later preferred quote.
 * - `all_fallback` – Every quote in the response was fallback-only; we returned API rank-1.
 */
export type RoutingOutcome = 'api_first_preferred' | 'skipped_fallback_api_first' | 'all_fallback';

export type CrossChainQuoteSelection = {
  quote: CrossChainQuote;
  outcome: RoutingOutcome;
};

/**
 * A quote is "direct" when no step is a DEX swap. Bridge, wrap, and unwrap
 * steps are allowed: they carry no MEV exposure, and a failed bridge refunds
 * the origin sellToken rather than a post-swap token (MAT-7080).
 */
export const isDirectQuote = (quote: CrossChainQuote): boolean =>
  quote.steps.every((step) => step.type !== 'swap');

export const getBridgeProvider = (steps: CrossChainStep[]): string | undefined =>
  steps.find((step) => step.type === 'bridge')?.provider;

const PREFERRED_MAYAN_PROVIDERS = new Set(['mayan_swift']);

const isFallbackProvider = (provider: string): boolean => {
  if (provider === 'bungee_auto') return true;
  if (provider.startsWith('mayan')) return !PREFERRED_MAYAN_PROVIDERS.has(provider);
  return false;
};

/**
 * A quote is "fallback-only" when every bridge step uses a fallback provider.
 * Quotes with no bridge steps are treated as preferred (not deprioritised).
 */
const isFallbackOnlyQuote = (quote: CrossChainQuote): boolean => {
  const bridgeSteps = quote.steps.filter((s) => s.type === 'bridge');
  if (bridgeSteps.length === 0) return false;
  return bridgeSteps.every((s) => isFallbackProvider(s.provider));
};

/**
 * Select the best cross-chain quote by deprioritising fallback providers
 * (`bungee_auto` and Mayan variants other than `mayan_swift`).
 *
 * Returns the first quote in API order that is **not** fallback-only.
 * If every quote is fallback-only, returns `quotes[0]` (the API's best).
 */
export const selectCrossChainQuote = (quotes: CrossChainQuote[]): CrossChainQuoteSelection => {
  const firstPreferred = quotes.find((q) => !isFallbackOnlyQuote(q));

  if (firstPreferred) {
    const outcome: RoutingOutcome =
      firstPreferred === quotes[0] ? 'api_first_preferred' : 'skipped_fallback_api_first';
    return { quote: firstPreferred, outcome };
  }

  return { quote: quotes[0], outcome: 'all_fallback' };
};
